import { extractCognitoTokens } from '@lastbrand/fe-common/universal-auth'

import { CONFIG_B2B_SELF_SERVICE_DOMAIN } from '@src/config/env'

import {
  B2B_TENANT_COOKIE,
  setCognitoTokenCookies,
  setIdTokenCookieForSSR,
  type CognitoTokenData,
} from './cross-domain-auth.helper'
import { isLocalhostHost } from './general.utils'

/**
 * Returns true when the URL string already contains a host (e.g. "https://…").
 * Path-only URLs like "/Quince-B2B-Self-Service?tenant=quince" return false.
 */
function hasHost(url: string): boolean {
  try {
    const parsed = new URL(url)
    return !!parsed.hostname
  } catch {
    return false
  }
}

/**
 * Derives the B2B self-serve preview domain from a D2C preview hostname.
 *
 * D2C preview pattern:  <branch>.preview-<env>.quince.com
 * B2B preview pattern:  <tenant>-<branch>-shop.preview-<env>.quince.com
 *
 * Returns undefined if the hostname is not a D2C preview domain or tenant is not provided.
 */
export function getB2BPreviewDomain(d2cHostname: string, tenant?: string): string | undefined {
  if (!tenant || !d2cHostname.includes('preview-')) return undefined

  const firstDot = d2cHostname.indexOf('.')
  if (firstDot === -1) return undefined

  const branch = d2cHostname.substring(0, firstDot) // e.g. "pm-cart-b2b"
  const baseDomain = d2cHostname.substring(firstDot + 1) // e.g. "preview-staging.quince.com"

  return `${tenant}-${branch}-shop.${baseDomain}`
}

/**
 * Resolves the full navigation URL from a Contentful-configured path.
 *
 * If the URL already has a host, returns it as-is.
 * Otherwise builds the host from the current D2C hostname (preview) or CONFIG_B2B_SELF_SERVICE_DOMAIN.
 * Extracts `tenant` query param from the URL to pass to getB2BPreviewDomain when available.
 *
 * For localhost, prepends /b2b/ to the path (no rewrite on localhost).
 */
function resolveB2BUrl(destinationUrl: string, isLocalhost: boolean): string {
  if (hasHost(destinationUrl)) {
    try {
      const parsed = new URL(destinationUrl)
      const isAllowedProtocol =
        parsed.protocol === 'https:' || (parsed.hostname === 'localhost' && parsed.protocol === 'http:')
      if (!isAllowedProtocol || (!parsed.hostname.endsWith('.quince.com') && parsed.hostname !== 'localhost')) {
        return '/'
      }
    } catch {
      return '/'
    }
    return destinationUrl
  }

  if (isLocalhost) {
    // eslint-disable-next-line i18next/no-literal-string
    return `/b2b${destinationUrl.startsWith('/') ? '' : '/'}${destinationUrl}`
  }

  // Extract tenant from query params if present
  const searchParams = new URLSearchParams(destinationUrl.split('?')[1] ?? '')
  const tenant = searchParams.get('tenant') ?? undefined

  const hostname = window.location.hostname
  const b2bDomain = getB2BPreviewDomain(hostname, tenant) ?? CONFIG_B2B_SELF_SERVICE_DOMAIN

  return `${window.location.protocol}//${b2bDomain}${destinationUrl.startsWith('/') ? '' : '/'}${destinationUrl}`
}

/**
 * Navigate from D2C to B2B self-serve, transferring auth tokens via cookies.
 *
 * The destinationUrl (from Contentful button link) is resolved against the current environment.
 */
export function navigateToB2BSelfServe(destinationUrl: string): void {
  const isLocalhost = isLocalhostHost(window.location.host)

  // eslint-disable-next-line @typescript-eslint/no-unsafe-call -- fe-common package exports not resolved by eslint
  const tokens = extractCognitoTokens() as CognitoTokenData

  if (tokens.refreshToken ?? tokens.idToken) {
    setCognitoTokenCookies(tokens)
  }

  if (tokens.idToken) {
    setIdTokenCookieForSSR(tokens.idToken)
  }

  const tenantFromUrl = new URLSearchParams(destinationUrl.split('?')[1] ?? '').get('tenant')
  if (tenantFromUrl) {
    const isSecure = window.location.protocol === 'https:'
    // eslint-disable-next-line i18next/no-literal-string -- cookie attribute string, not UI text
    const secureFlag = isSecure ? '; Secure' : ''
    // eslint-disable-next-line @quince/no-direct-cookie -- persisting tenant for SSR on localhost/preview, i18next/no-literal-string -- cookie attribute
    document.cookie = `${B2B_TENANT_COOKIE}=${encodeURIComponent(tenantFromUrl)}; path=/; SameSite=Lax${secureFlag}`
  }

  window.location.href = resolveB2BUrl(destinationUrl, isLocalhost)
}
